import { Button, Divider, Flex, Typography } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import type { ActiveIdp, ProviderResolved } from "shell/api/types";

import { Loading } from "design-system/components/Loading/Loading";
import {
  type AuthProvider,
  getUserManager,
  storeAuthProvider,
  storeCognitoAuthMode,
} from "shell/api/auth";

import s from "./ProviderOptions.module.css";

const { Text } = Typography;

interface ProviderOptionsProps {
  email: string;
  onPasswordLogin: (email: string) => void;
  onProviderSelected: (provider: AuthProvider, email?: string) => void;
  onReset: () => void;
  resolution: ProviderResolved;
}

export const ProviderOptions = ({
  email,
  onProviderSelected,
  onPasswordLogin,
  onReset,
  resolution,
}: ProviderOptionsProps) => {
  const { t } = useTranslation(["common"]);
  const { provider, idps, showPasswordOption } = resolution;

  const isUnspecified = provider === "unspecified";
  const isCloudEntity = provider === "cloudentity";
  const showCognitoOptions = provider === "cognito" || isUnspecified;
  const showCloudEntity = isCloudEntity || isUnspecified;

  useEffect(() => {
    if (isCloudEntity) {
      onProviderSelected("cloudentity", email);
    }
  }, [isCloudEntity, email, onProviderSelected]);

  if (isCloudEntity) {
    return (
      <Flex justify="center">
        <Loading size="large" />
      </Flex>
    );
  }

  const signinWithCognitoSso = (idp: ActiveIdp) => {
    storeAuthProvider("cognito");
    storeCognitoAuthMode("hosted");
    const manager = getUserManager();
    manager.signinRedirect({
      extraQueryParams: { identity_provider: idp.cognitoProviderName },
    });
  };

  return (
    <Flex vertical gap="middle" className={s.fullWidth}>
      <Text type="secondary">
        {t("common:idpDiscovery.signingInAs", { email })}
      </Text>

      {isUnspecified && <Text>{t("common:idpDiscovery.chooseProvider")}</Text>}

      {showCognitoOptions &&
        idps.map(idp => (
          <Button
            key={idp.cognitoProviderName}
            block
            size="large"
            type="primary"
            onClick={() => signinWithCognitoSso(idp)}
          >
            {idp.name}
          </Button>
        ))}

      {showPasswordOption && (
        <Button
          block
          size="large"
          type={idps.length === 0 ? "primary" : "default"}
          onClick={() => onPasswordLogin(email)}
        >
          {isUnspecified
            ? t("common:idpDiscovery.signInWithCognito")
            : t("common:idpDiscovery.signInWithPassword")}
        </Button>
      )}

      {showCloudEntity && (
        <Button
          block
          size="large"
          type="default"
          onClick={() => onProviderSelected("cloudentity", email)}
        >
          {t("common:idpDiscovery.signInWithCloudEntity")}
        </Button>
      )}

      <Divider>{isUnspecified ? null : t("common:idpDiscovery.or")}</Divider>

      <Button type="link" onClick={onReset} className={s.changeEmailLink}>
        {t("common:idpDiscovery.useDifferentEmail")}
      </Button>
    </Flex>
  );
};
